// Deliberate radial/orbital diagram for the Our Group page: one centre
// node (Gopi Gadhvi Group) surrounded by exactly six outer nodes, evenly
// spaced 60 degrees apart at an identical radius. The four active
// disciplines render as solid, confident neumorphic nodes; Technology
// and Investment are visually distinguished as future-facing (dashed
// connector + muted node + "Future" label) while remaining part of the
// same elegant composition.
import gggLogo from "@/assets/logos/ggg-logo-final.webp";

const ACTIVE_NODES = ["Property", "Design", "Creativity", "Transformation"];
const FUTURE_NODES = ["Technology", "Investment"];
const ALL_NODES = [...ACTIVE_NODES, ...FUTURE_NODES];

const RADIUS = 40;

const toXY = (angleDeg, radius) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: 50 + radius * Math.cos(rad), y: 50 + radius * Math.sin(rad) };
};

const GroupOrbitDiagram = () => {
  return (
    <>
      <div className="orbit-diagram hidden sm:block" data-testid="ecosystem-diagram">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" aria-hidden="true">
          {ALL_NODES.map((label, i) => {
            const angle = i * 60 - 90;
            const p = toXY(angle, RADIUS);
            const isFuture = FUTURE_NODES.includes(label);
            return (
              <line
                key={label}
                x1="50" y1="50" x2={p.x} y2={p.y}
                stroke={isFuture ? "rgba(216,180,106,0.28)" : "var(--gg-logo-gold)"}
                strokeOpacity={isFuture ? 1 : 0.55}
                strokeWidth="0.4"
                strokeDasharray={isFuture ? "1.5 1.6" : "none"}
              />
            );
          })}
        </svg>

        <div className="orbit-center" data-testid="ecosystem-node-center">
          <img src={gggLogo} alt="Gopi Gadhvi Group" className="max-w-[70%] max-h-[70%] object-contain" />
        </div>

        {ALL_NODES.map((label, i) => {
          const angle = i * 60 - 90;
          const p = toXY(angle, RADIUS);
          const isFuture = FUTURE_NODES.includes(label);
          return (
            <div
              key={label}
              className={`orbit-node ${isFuture ? "orbit-node-future" : ""}`}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
              data-testid={`ecosystem-node-${label.toLowerCase()}`}
            >
              <span className="text-small font-semibold" style={{ color: isFuture ? "var(--gg-muted-text)" : "var(--gg-ivory)" }}>{label}</span>
              {isFuture && <span className="eyebrow orbit-future-tag">Future</span>}
            </div>
          );
        })}
      </div>

      <div className="sm:hidden" data-testid="ecosystem-diagram-mobile">
        <div className="orbit-center-mobile mx-auto mb-8">
          <img src={gggLogo} alt="Gopi Gadhvi Group" className="max-w-[70%] max-h-[70%] object-contain" />
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {ACTIVE_NODES.map((label) => (
            <div key={label} className="neu-surface orbit-node-mobile" data-testid={`ecosystem-node-mobile-${label.toLowerCase()}`}>
              <span className="text-small font-semibold" style={{ color: "var(--gg-ivory)" }}>{label}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-3">
          {FUTURE_NODES.map((label) => (
            <div key={label} className="neu-surface orbit-node-mobile orbit-node-mobile-future">
              <span className="text-small" style={{ color: "var(--gg-muted-text)" }}>{label}</span>
              <span className="eyebrow orbit-future-tag">Future</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default GroupOrbitDiagram;
